import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, X, ChevronDown, ChevronUp, Upload, Download, FileText, Eye, FileSpreadsheet, FileOutput, Filter, LayoutGrid, List, LayoutList, TrendingDown, DollarSign, Activity, Coins, TrendingUp, Target, Calendar, Layers, XCircle, AlertTriangle } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getFluxTresorerie,
  createFluxTresorerie,
  updateFluxTresorerie,
  deleteFluxTresorerie } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { CustomDatePicker } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import {
  EMPTY_FLUX_FORM,
  buildFluxPayload,
  recordToFormData } from './08-flux-tresorerie/fluxTresoreriePayload';
import {
  formatComptabiliteDate,
  formatComptabiliteMoney,
  getComptabiliteDateLocale,
  getComplianceFrameworkKey } from './utils/comptabiliteSaudiFormat';
import '../../core/styles/global-dropdowns.css';

const FLUX_LIST_COLUMNS = [
  { key: 'flux_ref', labelKey: 'col_reference' },
  { key: 'exercice_fiscal', labelKey: 'col_exercice' },
  { key: 'periode', labelKey: 'col_periode' },
  { key: 'flux_exploitation_net', labelKey: 'col_flux_exploitation' },
  { key: 'variation_tresorerie', labelKey: 'col_variation' },
  { key: 'statut_flux', labelKey: 'col_statut' },
];

const FluxTresorerieSituationPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.fluxTresorerieSituationPage.${key}`, opts);
  const pc = (key, opts) => t(`modules.comptabilite.pages.common.${key}`, opts);
  const dateLocale = getComptabiliteDateLocale(i18n.language);
  const fmtMoney = (value) => formatComptabiliteMoney(value);

  const translateStatut = (val) => {
    const map = {
      Provisoire: p('status_provisoire'),
      Définitif: p('status_definitif'),
      Validé: p('status_valide') };
    return map[val] || p('status_provisoire');
  };

  const translatePeriode = (val) => {
    const map = {
      Mensuel: p('period_monthly'),
      Trimestriel: p('period_quarterly'),
      Annuel: p('period_annual') };
    return map[val] || val || p('em_dash');
  };

  const [fluxList, setFluxList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('flux_ref', (row) => row.flux_ref ?? ''),
    textColumnRule('exercice_fiscal', (row) => row.exercice_fiscal ?? ''),
    textColumnRule('periode', (row) => row.periode_debut ?? row.type_periode ?? ''),
    textColumnRule('flux_exploitation_net', (row) => String(row.flux_exploitation_net ?? '')),
    textColumnRule('variation_tresorerie', (row) => String(row.variation_tresorerie ?? '')),
  ], []);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [setDateRange] = useState({ start: null, end: null });
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_flux');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const [fluxTresorerieSituationFormTab, setFluxTresorerieSituationFormTab] = useState('infoFlux');
  const fluxTresorerieSituationFormTabs = useMemo(() => ([
    { id: 'infoFlux', label: p('section_info_flux'), icon: ChevronUp },
    { id: 'exploitation', label: p('section_flux_exploitation'), icon: ChevronUp },
    { id: 'investissementRatios', label: p('section_ratios_previsions'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_FLUX_FORM });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadFlux();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadFlux = async () => {
    try {
      const response = await getFluxTresorerie();
      setFluxList(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading flux:', error);
      setFluxList([]);
    }
  };
  
  const stats = {
    total: fluxList.length,
    fluxExploitation: fluxList.reduce((sum, f) => sum + (parseFloat(f.flux_exploitation_net) || 0), 0),
    fluxInvestissement: fluxList.reduce((sum, f) => sum + (parseFloat(f.flux_investissement_net) || 0), 0),
    fluxFinancement: fluxList.reduce((sum, f) => sum + (parseFloat(f.flux_financement_net) || 0), 0),
    variationTresorerie: fluxList.reduce((sum, f) => sum + (parseFloat(f.variation_tresorerie) || 0), 0),
    tresorerieDisponible: fluxList.reduce((sum, f) => sum + (parseFloat(f.flux_tresorerie_disponible) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_flux', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'par_periode', name: p('tab_by_period'), icon: Calendar },
    { id: 'par_statut', name: p('tab_by_status'), icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'provisoire', name: p('filter_provisoire'), icon: AlertTriangle },
    { id: 'definitif', name: p('filter_definitif'), icon: CheckCircle },
    { id: 'valide', name: p('filter_valide'), icon: CheckCircle }
  ];

  const fluxDistribution = {
    all_flux: {
      title: p('dist_overview_title'),
      data: [
        { key: 'Provisoire', name: p('status_provisoire'), count: fluxList.filter(f => f.statut_flux === 'Provisoire').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { key: 'Définitif', name: p('status_definitif'), count: fluxList.filter(f => f.statut_flux === 'Définitif').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { key: 'Validé', name: p('status_valide'), count: fluxList.filter(f => f.statut_flux === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_periode: {
      title: p('dist_by_period_title'),
      data: [
        { key: 'Mensuel', name: p('period_monthly'), count: fluxList.filter(f => f.type_periode === 'Mensuel').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { key: 'Trimestriel', name: p('period_quarterly'), count: fluxList.filter(f => f.type_periode === 'Trimestriel').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { key: 'Annuel', name: p('period_annual'), count: fluxList.filter(f => f.type_periode === 'Annuel').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { key: 'Provisoire', name: p('status_provisoire'), count: fluxList.filter(f => f.statut_flux === 'Provisoire').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { key: 'Définitif', name: p('status_definitif'), count: fluxList.filter(f => f.statut_flux === 'Définitif').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { key: 'Validé', name: p('status_valide'), count: fluxList.filter(f => f.statut_flux === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    }
  };

  const distributionInfo = fluxDistribution[mainViewMode] || fluxDistribution.all_flux;
  
  const filteredFlux = useMemo(() => {
    const base = fluxList.filter(f => {

      if (searchTerm && !f.flux_ref?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !f.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !f.type_periode?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      if (mainViewMode === 'all_flux') {
        if (quickFilter === 'provisoire' && f.statut_flux !== 'Provisoire') return false;
        if (quickFilter === 'definitif' && f.statut_flux !== 'Définitif') return false;
        if (quickFilter === 'valide' && f.statut_flux !== 'Validé') return false;
      }
      if (activeDistributionFilter) {
        if (mainViewMode === 'par_statut' || mainViewMode === 'all_flux') {
          if (f.statut_flux !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'par_periode') {
          if (f.type_periode !== activeDistributionFilter) return false;
        }
      }

      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [fluxList, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredFlux, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(FLUX_LIST_COLUMNS, p);
  const fluxTableColSpan = visibleDataColumnCount + 2;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = buildFluxPayload(formData);
      if (editingRecord) {
        await updateFluxTresorerie(editingRecord.id, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'جدول التدفقات' }));
      } else {
        await createFluxTresorerie(submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'جدول التدفقات' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      resetForm();
      loadFlux();
    }
  };
  
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData(recordToFormData(record));
    setFluxTresorerieSituationFormTab('infoFlux');
    setShowModal(true);
  };

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'Provisoire', labelKey: 'status_provisoire', icon: AlertTriangle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'Définitif', labelKey: 'status_definitif', icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', labelKey: 'status_valide', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ], p), [t]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const record = fluxList.find((f) => f.id === recordId);
      if (!record) return;
      await updateFluxTresorerie(recordId, { ...record, statut_flux: newStatus });
      loadFlux();
    } catch (error) {
      console.error('Error updating flux status:', error);
    }
  };
  
  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await deleteFluxTresorerie(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'جدول التدفقات' }));
        loadFlux();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteFluxTresorerie(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'جداول التدفقات' }));
        setSelectedRecords([]);
        clearSelection();
        loadFlux();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };
  
  const resetForm = () => {
    setFormData({ ...EMPTY_FLUX_FORM });
    setEditingRecord(null);
    setShowModal(false);
    setFluxTresorerieSituationFormTab('infoFlux');
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      ...EMPTY_FLUX_FORM,
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setFluxTresorerieSituationFormTab('infoFlux');
    setShowModal(true);
  };
  
  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Wallet className="h-8 w-8 text-cyan-400" />
            {p('title')}
            <button onClick={loadFlux} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-cyan-500 to-cyan-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-cyan-600 hover:to-cyan-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={openCreateModal}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_flux')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total_flux'), value: stats.total, icon: Wallet, color: 'from-indigo-500 to-indigo-700' },
          { title: p('stat_flux_exploitation'), value: fmtMoney(stats.fluxExploitation), icon: Activity, color: 'from-cyan-500 to-cyan-600' },
          { title: p('stat_flux_investissement'), value: fmtMoney(stats.fluxInvestissement), icon: TrendingDown, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_flux_financement'), value: fmtMoney(stats.fluxFinancement), icon: DollarSign, color: 'from-emerald-500 to-emerald-600' },
          { title: p('stat_variation_tresorerie'), value: fmtMoney(stats.variationTresorerie), icon: Activity, color: 'from-green-500 to-green-600' },
          { title: p('stat_tresorerie_disponible'), value: fmtMoney(stats.tresorerieDisponible), icon: Coins, color: 'from-amber-500 to-amber-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_flux' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.key} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.key ? null : item.key)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.key ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.key ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.key ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? p('percent_of_total', { percent: (item.count / total * 100).toFixed(1) }) : p('percent_of_total', { percent: 0 });
                  })()}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('flux_ref') ? (
                    <TableColumnHeaderFilter columnKey="flux_ref" label={p('col_reference')} value={columnFilters.flux_ref || ''} onChange={(v) => setColumnFilter('flux_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('exercice_fiscal') ? (
                    <TableColumnHeaderFilter columnKey="exercice_fiscal" label={p('col_exercice')} value={columnFilters.exercice_fiscal || ''} onChange={(v) => setColumnFilter('exercice_fiscal', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('periode') ? (
                    <TableColumnHeaderFilter columnKey="periode" label={p('col_periode')} value={columnFilters.periode || ''} onChange={(v) => setColumnFilter('periode', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('flux_exploitation_net') ? (
                    <TableColumnHeaderFilter columnKey="flux_exploitation_net" label={p('col_flux_exploitation')} value={columnFilters.flux_exploitation_net || ''} onChange={(v) => setColumnFilter('flux_exploitation_net', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('variation_tresorerie') ? (
                    <TableColumnHeaderFilter columnKey="variation_tresorerie" label={p('col_variation')} value={columnFilters.variation_tresorerie || ''} onChange={(v) => setColumnFilter('variation_tresorerie', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_flux') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_statut')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredFlux.length > 0 ? (
                  filteredFlux.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('flux_ref') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.flux_ref}</span></td>
                      ) : null}
                      {isColVisible('exercice_fiscal') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.exercice_fiscal}</span></td>
                      ) : null}
                      {isColVisible('periode') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.periode_debut ? new Date(record.periode_debut).toLocaleDateString(dateLocale) : '-'}</span>
                          <span className="text-white/50 text-xs">{record.periode_fin ? new Date(record.periode_fin).toLocaleDateString(dateLocale) : '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('flux_exploitation_net') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-bold">{fmtMoney(record.flux_exploitation_net || 0)}</span>
                          <span className="text-white/50 text-xs">{translatePeriode(record.type_periode)}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('variation_tresorerie') ? (
                      <td className="px-6 py-4">
                        <span className={`text-white font-bold ${(record.variation_tresorerie || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {fmtMoney(record.variation_tresorerie || 0)}
                        </span>
                      </td>
                      ) : null}
                      {isColVisible('statut_flux') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={record.statut_flux || 'Provisoire'}
                            recordId={record.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={fluxTableColSpan} className="text-center py-12 text-white/60">
                      <Wallet size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_state')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredFlux.length > 0 ? (
              filteredFlux.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Wallet size={20} className="text-cyan-400" />
                          {record.flux_ref}
                        </h3>
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                          record.statut_flux === 'Validé' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          record.statut_flux === 'Définitif' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                          'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}>
                          {translateStatut(record.statut_flux || 'Provisoire')}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}:</span> {record.flux_ref}</div>
                            <div><span className="font-medium">{p('label_exercice')}:</span> {record.exercice_fiscal}</div>
                            <div><span className="font-medium">{p('label_type')}:</span> {translatePeriode(record.type_periode)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_periode')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_debut')}:</span> {record.periode_debut ? new Date(record.periode_debut).toLocaleDateString(dateLocale) : p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_fin')}:</span> {record.periode_fin ? new Date(record.periode_fin).toLocaleDateString(dateLocale) : p('em_dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_flux')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_exploitation')}:</span> {fmtMoney(record.flux_exploitation_net || 0)}</div>
                            <div><span className="font-medium">{p('label_investissement')}:</span> {fmtMoney(record.flux_investissement_net || 0)}</div>
                            <div><span className="font-medium">{p('label_financement')}:</span> {fmtMoney(record.flux_financement_net || 0)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_tresorerie')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_variation')}:</span> <span className={`font-bold ${(record.variation_tresorerie || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(record.variation_tresorerie || 0)}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Wallet size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_state')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Wallet size={18} className="text-cyan-400" />{p('section_flux_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('col_reference')}</p><p className="text-white font-medium">{viewingRecord.flux_ref}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_exercice')}</p><p className="text-white font-medium">{viewingRecord.exercice_fiscal}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_type_periode')}</p><p className="text-white font-medium">{translatePeriode(viewingRecord.type_periode)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_periode_debut')}</p><p className="text-white font-medium">{viewingRecord.periode_debut ? new Date(viewingRecord.periode_debut).toLocaleDateString(dateLocale) : p('em_dash')}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_periode_fin')}</p><p className="text-white font-medium">{viewingRecord.periode_fin ? new Date(viewingRecord.periode_fin).toLocaleDateString(dateLocale) : p('em_dash')}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_statut')}</p><p className="text-white font-medium">{translateStatut(viewingRecord.statut_flux || 'Provisoire')}</p></div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="flux-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-700 text-white hover:from-cyan-600 hover:to-cyan-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-cyan-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {fluxTresorerieSituationFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = fluxTresorerieSituationFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setFluxTresorerieSituationFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="flux-form" onSubmit={handleSubmit}>
                {fluxTresorerieSituationFormTab === 'infoFlux' && (
                  <div className="space-y-6">
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <ComptabiliteFiscalYearField label={p('label_exercice_fiscal')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_exercice')} required />
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_debut')}</label><CustomDatePicker value={formData.periode_debut} onChange={(dateString) => setFormData({ ...formData, periode_debut: dateString })} placeholder={p('select')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_fin')}</label><CustomDatePicker value={formData.periode_fin} onChange={(dateString) => setFormData({ ...formData, periode_fin: dateString })} placeholder={p('select')} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_periode')}</label><CustomSelect value={formData.type_periode} onChange={(value) => setFormData({ ...formData, type_periode: value })} options={[
                              { value: 'Mensuel', label: p('period_monthly'), isDefault: true },
                              { value: 'Trimestriel', label: p('period_quarterly'), isDefault: true },
                              { value: 'Annuel', label: p('period_annual'), isDefault: true }
                            ]} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_methode_calcul')}</label><CustomSelect value={formData.methode_calcul} onChange={(value) => setFormData({ ...formData, methode_calcul: value })} options={[
                              { value: 'Direct', label: p('method_direct'), isDefault: true },
                              { value: 'Indirect', label: p('method_indirect'), isDefault: true }
                            ]} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_statut')}</label><CustomSelect value={formData.statut_flux} onChange={(value) => setFormData({ ...formData, statut_flux: value })} options={[
                              { value: 'Provisoire', label: p('status_provisoire'), isDefault: true },
                              { value: 'Définitif', label: p('status_definitif'), isDefault: true },
                              { value: 'Validé', label: p('status_valide'), isDefault: true }
                            ]} searchable={false} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_validation_tresorier')}</label><input type="text" value={formData.validation_tresorier} onChange={(e) => setFormData({ ...formData, validation_tresorier: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{pc(getComplianceFrameworkKey())}</label><CustomSelect value={formData.conformite_pcn} onChange={(value) => setFormData({ ...formData, conformite_pcn: value })} options={[
                              { value: 'Oui', label: p('yes'), isDefault: true },
                              { value: 'Non', label: p('no'), isDefault: true }
                            ]} searchable={false} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all" /></div>
                          </div>
                  </div>
                </div>
                )}
                {fluxTresorerieSituationFormTab === 'exploitation' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_encaissements')}</label><CustomNumberInput step={0.01} value={formData.flux_exploitation_encaissements} onChange={(value) => setFormData({ ...formData, flux_exploitation_encaissements: value })} placeholder={p('placeholder_amount')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_decaissements')}</label><CustomNumberInput step={0.01} value={formData.flux_exploitation_decaissements} onChange={(value) => setFormData({ ...formData, flux_exploitation_decaissements: value })} placeholder={p('placeholder_amount')} /></div>
                          </div>
                )}
                {fluxTresorerieSituationFormTab === 'investissementRatios' && (
                  <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_acquisitions')}</label><CustomNumberInput step={0.01} value={formData.flux_investissement_acquisitions} onChange={(value) => setFormData({ ...formData, flux_investissement_acquisitions: value })} placeholder={p('placeholder_amount')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_cessions')}</label><CustomNumberInput step={0.01} value={formData.flux_investissement_cessions} onChange={(value) => setFormData({ ...formData, flux_investissement_cessions: value })} placeholder={p('placeholder_amount')} /></div>
                          </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tresorerie_debut')}</label><CustomNumberInput step={0.01} value={formData.tresorerie_debut_periode} onChange={(value) => setFormData({ ...formData, tresorerie_debut_periode: value })} placeholder={p('placeholder_amount')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_delai_recouvrement')}</label><CustomNumberInput step={1} value={formData.delai_recouvrement_clients} onChange={(value) => setFormData({ ...formData, delai_recouvrement_clients: value })} placeholder={p('placeholder_days')} /></div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default FluxTresorerieSituationPage;

